import {
  memo,
  useCallback,
  type CSSProperties,
  type MouseEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { Card, ActionIcon } from '@mantine/core';
import { IconCalendar, IconDotsVertical } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  STATUS_COLORS,
  type Task,
  type TaskListItem,
  TaskStatus,
} from '../../../types';
import { FormerMemberBadge } from '@/components/common/FormerMemberBadge';
import { ProgressSection } from '@/components/ui/ProgressSection';
import { StatusBadge } from '@/components/common/StatusBadge';
import { UserAvatar } from '@/components/user/UserAvatar';
import { truncateText } from '../../../utils/helpers';
import { useLocalizedDate } from '../../../hooks/useLocalizedDate';
import { useTaskNavigation } from '../../../hooks';
import { useGeoMarkupStore } from '../../../store';
import { ICON_SIZE_TAB } from '@/constants/ui';
import styles from './TaskCard.module.css';

const TITLE_MAX_LENGTH = 40;

interface TaskCardProps {
  task: TaskListItem | Task;
  showContextMenu?: boolean;
  isMenuOpen?: boolean;
  onMenuToggle?: (taskId: string, element: HTMLButtonElement) => void;
  onClick?: () => void;
  disableSelection?: boolean;
  menuButtonRef?: RefObject<HTMLButtonElement | null>;
}

function TaskCardComponent({
  task,
  showContextMenu = false,
  isMenuOpen = false,
  onMenuToggle,
  onClick,
  disableSelection = false,
  menuButtonRef,
}: TaskCardProps) {
  const { t } = useTranslation();
  const { formatDateCompact, isOverdue } = useLocalizedDate();
  const { navigateToTaskDetails } = useTaskNavigation();

  const selectedTaskId = useGeoMarkupStore((state) => state.tasks.selectedId);
  const isSelected = !disableSelection && selectedTaskId === task.id;

  const taskCardClassName = `${styles.taskCard} ${isSelected ? styles.selected : ''}`;

  const totalRequirements = task.requirementCount ?? 0;
  const completedRequirements = task.completedRequirementCount ?? 0;

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      const coordinates = task.anchorPoint || { lat: 0, lng: 0 };
      navigateToTaskDetails(task.shortId, coordinates);
    }
  }, [onClick, navigateToTaskDetails, task.shortId, task.anchorPoint]);

  const handleMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onMenuToggle?.(task.id, event.currentTarget);
    },
    [onMenuToggle, task.id]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <Card
      padding="sm"
      withBorder
      className={taskCardClassName}
      onClick={handleClick}
      onKeyDown={handleKeyPress}
      tabIndex={0}
      role="button"
      aria-label={`${t('tasks.openDetails')}: ${task.title}`}
    >
      <div className={styles.cardBody}>
        <div className={styles.titleRow}>
          <span
            className={styles.statusDot}
            style={
              { '--dot-color': STATUS_COLORS[task.status] } as CSSProperties
            }
            aria-hidden="true"
          />
          <span className={styles.title}>
            {truncateText(task.title, TITLE_MAX_LENGTH)}
          </span>
          <div className={styles.badgeGroup}>
            <StatusBadge type="priority" value={task.priority} size="xs" />
            {showContextMenu && (
              <ActionIcon
                ref={menuButtonRef}
                variant="default"
                color="gray"
                size="sm"
                onClick={handleMenuClick}
                aria-label={t('common.more')}
                className={`${styles.menuButton} ${isMenuOpen ? styles.menuButtonActive : ''}`}
              >
                <IconDotsVertical size={16} />
              </ActionIcon>
            )}
          </div>
        </div>

        <div className={styles.metaSection}>
          <div className={styles.metaRow}>
            {task.assignedToName && (
              <div className={styles.metaGroup}>
                <UserAvatar
                  displayName={task.assignedToName}
                  avatarUrl={task.assignedToAvatarUrl}
                  size="xs"
                />
                <span className={styles.metaText}>{task.assignedToName}</span>
                {task.assignedToIsFormerMember && <FormerMemberBadge />}
              </div>
            )}

            {task.dueDate && (
              <div className={styles.metaGroup}>
                <IconCalendar
                  size={ICON_SIZE_TAB}
                  className={styles.metaIcon}
                />
                <span
                  className={
                    isOverdue(task.dueDate) &&
                    task.status !== TaskStatus.Completed
                      ? styles.overdueText
                      : styles.metaText
                  }
                >
                  {formatDateCompact(task.dueDate)}
                </span>
              </div>
            )}
          </div>

          {totalRequirements > 0 && (
            <ProgressSection
              label={t('fields.requirements')}
              completed={completedRequirements}
              total={totalRequirements}
              size="xs"
            />
          )}
        </div>
      </div>
    </Card>
  );
}

export const TaskCard = memo(TaskCardComponent);
